import React from 'react';
import { gettingStartedSteps } from '../mockData/mock';
import { Clock, CheckCircle, Lightbulb, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';

const GettingStarted = () => {
  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-[#f7f5f2] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-6">
            Start Your Sourdough Journey
          </h1>
          <p className="text-xl text-[#736c64] max-w-2xl mx-auto">
            Creating beautiful sourdough bread is easier than you think. 
            Let's walk through everything step by step.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-[#736c64] leading-relaxed">
              Welcome to the world of sourdough! Whether you're starting a new starter from scratch, 
              reviving a dormant one, or learning to maintain an active culture, this guide has you covered. 
              Remember: sourdough is forgiving, and every baker started exactly where you are now.
            </p>
          </div>
        </div>
      </section>

      {/* Step-by-Step Guides */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {gettingStartedSteps.map((guide, index) => (
              <div key={guide.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-[#a8b5a0]/10 to-[#a8b5a0]/5 p-8 border-b border-[#e8e6e1]">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="inline-flex items-center space-x-2 mb-4">
                        <div className="w-8 h-8 bg-[#a8b5a0] rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">{index + 1}</span>
                        </div>
                        <span className="text-sm font-medium text-[#736c64]">Guide {index + 1}</span>
                      </div>
                      <h2 className="text-3xl font-bold text-[#1e1919] mb-2">
                        {guide.title}
                      </h2>
                      <p className="text-[#736c64] text-lg">
                        {guide.description}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-2">
                      <Clock className="h-5 w-5 text-[#a8b5a0]" />
                      <span className="text-sm font-medium text-[#736c64]">{guide.duration}</span>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  {/* Steps */}
                  <div className="space-y-6 mb-8">
                    {guide.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-[#a8b5a0]/20 rounded-full flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-[#a8b5a0]" />
                          </div>
                        </div>
                        <div className="flex-grow">
                          <h3 className="font-semibold text-[#1e1919] mb-2">{step.day}</h3>
                          <p className="text-[#736c64] leading-relaxed">{step.instruction}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tips */}
                  {guide.tips && guide.tips.length > 0 && (
                    <div className="bg-[#f7f5f2] rounded-xl p-6 border border-[#e8e6e1]">
                      <div className="flex items-center gap-2 mb-4">
                        <Lightbulb className="h-5 w-5 text-[#a8b5a0]" />
                        <h3 className="font-semibold text-[#1e1919]">Pro Tips</h3>
                      </div>
                      <ul className="space-y-2">
                        {guide.tips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="flex items-start gap-2 text-[#736c64]">
                            <span className="text-[#a8b5a0] mt-1">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Next Steps CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#a8b5a0] to-[#8fa087] rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Bake Your First Loaf?
            </h2>
            <p className="text-lg mb-8 text-white/90">
              Once your starter is active and bubbly, it's time to explore our beginner-friendly recipes.
            </p>
            <Link to="/recipes">
              <Button 
                size="lg" 
                className="bg-white text-[#a8b5a0] hover:bg-[#f7f5f2] px-8 py-6 text-lg group transition-all"
              >
                View Recipes
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#1e1919] mb-8 text-center">
            Need More Help?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/dictionary" className="group">
              <Card className="h-full hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1e1919] mb-2 group-hover:text-[#a8b5a0] transition-colors">
                    Sourdough Dictionary
                  </h3>
                  <p className="text-sm text-[#736c64]">
                    Learn the terminology and science behind the process.
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/troubleshooting" className="group">
              <Card className="h-full hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1e1919] mb-2 group-hover:text-[#a8b5a0] transition-colors">
                    Troubleshooting
                  </h3>
                  <p className="text-sm text-[#736c64]">
                    Find solutions to common starter and baking issues.
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/faq" className="group">
              <Card className="h-full hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1e1919] mb-2 group-hover:text-[#a8b5a0] transition-colors">
                    FAQ
                  </h3>
                  <p className="text-sm text-[#736c64]">
                    Quick answers to the most common questions.
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GettingStarted;
